{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "integrations",
  "title": "Integrations",
  "description": "Per-site integration toggles.",
  "type": "object",
  "x-feature": true,
  "x-access": "staff",
  "properties": {
    "calcom": {
      "type": "object",
      "ui:title": "Cal.com booking",
      "ui:description": "Show your Cal.com booking page from the chat. Requires In-chat booking.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable"
        },
        "mode": {
          "type": "string",
          "enum": ["in-chat", "popup"],
          "default": "in-chat",
          "ui:title": "How it opens",
          "ui:enumLabels": {
            "in-chat": "Embedded in chat",
            "popup": "Popup overlay"
          },
          "ui:description": "Embedded shows the calendar inline in the conversation. Popup opens it as an overlay on the page."
        }
      }
    },
    "calendly": {
      "type": "object",
      "ui:title": "Calendly booking",
      "ui:description": "Show your Calendly booking page from the chat. Requires In-chat booking.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable"
        },
        "mode": {
          "type": "string",
          "enum": ["in-chat", "popup"],
          "default": "in-chat",
          "ui:title": "How it opens",
          "ui:enumLabels": {
            "in-chat": "Embedded in chat",
            "popup": "Popup overlay"
          },
          "ui:description": "Embedded shows the calendar inline in the conversation. Popup opens it as an overlay on the page."
        }
      }
    },
    "vector": {
      "type": "object",
      "ui:title": "Vector.co",
      "ui:description": "Contact-level identification via Vector.co pixel. US traffic only. ~$0.10 per identified contact.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable Vector.co pixel",
          "ui:description": "Off by default. When on, the Vector.co pixel loads on this site. Cost is per identified contact — only enable for high-priority sites."
        }
      }
    },
    "slack": {
      "type": "object",
      "ui:title": "Slack",
      "ui:description": "Send conversations to a Slack channel — one thread per conversation, each visitor question and Rose answer posted as a reply inside it.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable Slack",
          "ui:description": "Off by default. When on, conversations are posted to the channel below."
        },
        "bot_token": {
          "type": "string",
          "default": "",
          "pattern": "^(xoxb-[A-Za-z0-9-]+)?$",
          "ui:title": "Bot token",
          "ui:description": "From your Slack app → OAuth & Permissions → Bot User OAuth Token. Starts with xoxb- and needs the chat:write permission. (An app-level token starting with xapp- will not work.)"
        },
        "channel_id": {
          "type": "string",
          "default": "",
          "ui:title": "Channel ID",
          "ui:description": "Where conversations are posted. In Slack, open the channel → its name → copy the Channel ID at the bottom (looks like C0123ABCD)."
        },
        "push_mode": {
          "type": "string",
          "enum": ["per_message", "digest", "qualified_only"],
          "default": "per_message",
          "ui:title": "What to post",
          "ui:description": "Per message: every question and answer, live in the thread. Digest: one summary when the conversation ends. Qualified only: only conversations where a visitor shares an email or asks for a demo.",
          "ui:enumDescriptions": {
            "per_message": "Every turn, live in the thread",
            "digest": "One summary at the end",
            "qualified_only": "Only conversations that show buying intent"
          }
        },
        "min_turns_before_push": {
          "type": "integer",
          "default": 1,
          "minimum": 1,
          "ui:title": "Minimum messages before posting",
          "ui:description": "Wait until a conversation reaches this many visitor messages before posting it to Slack. Keep at 1 to post from the first question; raise it to skip one-off visitors."
        },
        "environments": {
          "type": "string",
          "enum": ["staging_and_production", "production_only", "all"],
          "default": "staging_and_production",
          "ui:title": "Send from",
          "ui:description": "Which environments post to Slack. Staging and production (default) keeps internal test traffic out of your channel. Production only is the strictest. All also includes the internal test environment.",
          "ui:enumDescriptions": {
            "staging_and_production": "Staging and production",
            "production_only": "Production only",
            "all": "All environments (includes internal test)"
          }
        }
      }
    },
    "intercom": {
      "type": "object",
      "ui:title": "Intercom",
      "ui:description": "When on, Rose answers support questions by offering to open your Intercom messenger instead of handing out an email. Requires the Intercom messenger to already load on your site.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable Intercom hand-off",
          "ui:description": "Off by default. When on, support-intent answers offer a link that opens your Intercom messenger on the visitor's current page."
        },
        "default_space": {
          "type": "string",
          "enum": ["home", "messages", "help", "tickets"],
          "default": "messages",
          "ui:title": "Which space to open",
          "ui:description": "Where the messenger opens. Messages starts a new conversation, Help opens your help center, Home is the messenger landing, Tickets opens ticket status.",
          "ui:enumDescriptions": {
            "home": "Messenger home",
            "messages": "New conversation",
            "help": "Help center",
            "tickets": "Ticket status"
          }
        },
        "fallback_url": {
          "type": "string",
          "default": "",
          "pattern": "^(https://.+)?$",
          "ui:title": "Fallback URL (optional)",
          "ui:description": "Where the visitor is sent if the Intercom messenger is not loaded on the page they are on (for example your help center). Leave empty to do nothing when the messenger is unavailable."
        }
      }
    },
    "identified_accounts_webhook": {
      "type": "object",
      "ui:title": "Identified accounts webhook",
      "ui:description": "Send every company we identify visiting your site to your own endpoint (CRM, n8n, Zapier, ...) within a few minutes of it being identified.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable webhook",
          "ui:description": "Off by default. When on, each newly identified company account is sent to the URL below within a few minutes."
        },
        "webhook_url": {
          "type": "string",
          "default": "",
          "pattern": "^(https://.+)?$",
          "ui:title": "Webhook URL",
          "ui:description": "The HTTPS address that receives each identified account. We send a POST with the company details (name, domain, sector, size, ...) as JSON. Example: https://hooks.zapier.com/hooks/catch/123/abc."
        },
        "secret": {
          "type": "string",
          "default": "",
          "ui:title": "Secret key (optional)",
          "ui:description": "A secret you choose to confirm each request really came from Rose. If set, we add a signature in the X-Webhook-Signature header that your endpoint can check. Leave empty to skip."
        },
        "only_with_conversation": {
          "type": "boolean",
          "default": false,
          "ui:title": "Only accounts that started a conversation",
          "ui:description": "Off by default: every identified company is sent, even if the visitor never chats. Turn on to only send accounts whose visitor actually started a conversation with Rose — fewer accounts, but warmer ones."
        }
      }
    },
    "identified_visitors_webhook": {
      "type": "object",
      "ui:title": "Identified visitors webhook",
      "ui:description": "Send every person we identify visiting your site to your own endpoint (CRM, n8n, Zapier, ...) within a few minutes of them being identified. This is the person-level counterpart of the identified accounts webhook: a named individual rather than a company.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable webhook",
          "ui:description": "Off by default. When on, each newly identified person is sent to the URL below within a few minutes. Only visitors we can name are sent — a visit where we recognise the company but not the person goes to the identified accounts webhook instead."
        },
        "webhook_url": {
          "type": "string",
          "default": "",
          "pattern": "^(https://.+)?$",
          "ui:title": "Webhook URL",
          "ui:description": "The HTTPS address that receives each identified person. We send a POST with their details (name, work email, job title, LinkedIn) plus their company and how they reached your site, as JSON. Example: https://hooks.zapier.com/hooks/catch/123/abc."
        }
      }
    },
    "scheduled_export_webhook": {
      "type": "object",
      "ui:title": "Scheduled export webhook",
      "ui:description": "Receive your conversations and accounts exports on your own endpoint every day or every week, instead of downloading the CSV files from the backoffice.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable scheduled export",
          "ui:description": "Off by default. When on, the previous day or week is sent to the URL below every morning or every Monday morning."
        },
        "webhook_url": {
          "type": "string",
          "default": "",
          "pattern": "^(https://.+)?$",
          "ui:title": "Webhook URL",
          "ui:description": "The HTTPS address that receives the exports. We send one POST per dataset and period, with the same rows as the backoffice CSV export, as JSON."
        },
        "frequency": {
          "type": "string",
          "enum": ["daily", "weekly"],
          "default": "weekly",
          "ui:title": "Frequency",
          "ui:enumLabels": {
            "daily": "Every day (previous day)",
            "weekly": "Every Monday (previous week)"
          }
        },
        "datasets": {
          "type": "array",
          "uniqueItems": true,
          "items": {
            "type": "string",
            "enum": ["conversations", "accounts"]
          },
          "default": ["conversations", "accounts"],
          "ui:title": "What to send",
          "ui:description": "Conversations: one row per conversation, with its transcript. Accounts: one row per company active in the period."
        },
        "timezone": {
          "type": "string",
          "default": "Europe/Paris",
          "ui:title": "Timezone",
          "ui:description": "Days and weeks start at midnight in this IANA timezone (for example Europe/Paris)."
        }
      }
    }
  },
  "default": {
    "calcom": {
      "enabled": false,
      "mode": "in-chat"
    },
    "calendly": {
      "enabled": false,
      "mode": "in-chat"
    },
    "vector": {
      "enabled": false
    },
    "slack": {
      "enabled": false,
      "bot_token": "",
      "channel_id": "",
      "push_mode": "per_message",
      "min_turns_before_push": 1
    },
    "intercom": {
      "enabled": false,
      "default_space": "messages",
      "fallback_url": ""
    },
    "identified_accounts_webhook": {
      "enabled": false,
      "webhook_url": "",
      "secret": "",
      "only_with_conversation": false
    },
    "identified_visitors_webhook": {
      "enabled": false,
      "webhook_url": ""
    },
    "scheduled_export_webhook": {
      "enabled": false,
      "webhook_url": "",
      "frequency": "weekly",
      "datasets": ["conversations", "accounts"],
      "timezone": "Europe/Paris"
    }
  }
}
